<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import StatCard from "@/modules/Dashboard/components/StatCard.vue";

export interface GenealogyStats {
  persons: {
    total: number;
    requiring_revision: number;
    by_sex: { H?: number; M?: number; F?: number; unknown: number };
  };
  events: {
    total: number;
    with_exact_date: number;
    with_poblacion_id: number;
    with_parroquia_id: number;
    year_range: { min: number | null; max: number | null };
  };
  participants: number;
  catalog: { poblaciones: number; parroquias: number };
  events_by_type: Record<"BAUTIZO" | "MATRIMONIO" | "DEFUNCION", number>;
  top_poblaciones: Array<{ nombre: string; count: number }>;
  top_parroquias: Array<{ nombre: string; count: number }>;
}

const props = defineProps<{
  stats: GenealogyStats;
}>();

const authStore = useAuthStore();
const isAdmin = computed(() => authStore.user?.role === "ADMIN");

const { t, locale } = useI18n();

const numberFmt = computed(
  () => new Intl.NumberFormat(locale.value === "en" ? "en" : "es")
);

const formatNum = (n: number): string => numberFmt.value.format(n);

const yearRangeLabel = computed(() => {
  const { min, max } = props.stats.events.year_range;
  if (min == null || max == null) {
    return "";
  }
  return t("dashboard.genealogy.year_range", { min, max });
});

const geoCoverageLabel = computed(() => {
  const total = props.stats.events.total;
  if (total === 0) {
    return "";
  }
  const pct = Math.round((props.stats.events.with_poblacion_id / total) * 100);
  return t("dashboard.genealogy.geo_coverage", { pct });
});

const maxTopCount = computed(() =>
  Math.max(1, ...props.stats.top_poblaciones.map((p) => p.count), ...props.stats.top_parroquias.map((p) => p.count))
);

const eventTypeBars = computed(() => {
  const types = ["BAUTIZO", "MATRIMONIO", "DEFUNCION"] as const;
  const max = Math.max(1, ...types.map((k) => props.stats.events_by_type[k] ?? 0));
  return types.map((tipo) => ({
    tipo,
    count: props.stats.events_by_type[tipo] ?? 0,
    pct: ((props.stats.events_by_type[tipo] ?? 0) / max) * 100,
    color:
      tipo === "BAUTIZO"
        ? "bg-blue-500"
        : tipo === "MATRIMONIO"
          ? "bg-emerald-500"
          : "bg-slate-500",
  }));
});

const maleCount = computed(() => Number(props.stats.persons.by_sex.H ?? 0));
const femaleCount = computed(
  () => Number(props.stats.persons.by_sex.M ?? 0) + Number(props.stats.persons.by_sex.F ?? 0)
);
const unknownSexCount = computed(() => Number(props.stats.persons.by_sex.unknown ?? 0));

const sexTotal = computed(() => maleCount.value + femaleCount.value + unknownSexCount.value);
</script>

<template>
  <div class="col-span-12 mt-6">
    <div class="intro-y flex items-center h-10">
      <h2 class="text-lg font-medium">{{ t("dashboard.genealogy.title") }}</h2>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mt-5">
      <component
        :is="isAdmin ? 'router-link' : 'div'"
        :to="isAdmin ? { name: 'genealogy.review' } : undefined"
        class="block"
      >
        <StatCard
          :title="t('dashboard.genealogy.persons')"
          :value="formatNum(stats.persons.total)"
          icon="Users"
          variant="primary"
          :sub-value="
            stats.persons.requiring_revision > 0
              ? t('dashboard.genealogy.pending_review', {
                  count: formatNum(stats.persons.requiring_revision),
                })
              : undefined
          "
        />
      </component>
      <StatCard
        :title="t('dashboard.genealogy.events')"
        :value="formatNum(stats.events.total)"
        icon="CalendarDays"
        variant="info"
        :sub-value="yearRangeLabel || undefined"
      />
      <StatCard
        :title="t('dashboard.genealogy.baptisms')"
        :value="formatNum(stats.events_by_type.BAUTIZO)"
        icon="Baby"
        variant="info"
      />
      <StatCard
        :title="t('dashboard.genealogy.deaths')"
        :value="formatNum(stats.events_by_type.DEFUNCION)"
        icon="Cross"
        variant="secondary"
      />
      <StatCard
        :title="t('dashboard.genealogy.marriages')"
        :value="formatNum(stats.events_by_type.MATRIMONIO)"
        icon="Heart"
        variant="success"
      />
      <StatCard
        :title="t('dashboard.genealogy.participants')"
        :value="formatNum(stats.participants)"
        icon="GitBranch"
        variant="purple"
      />
      <StatCard
        :title="t('dashboard.genealogy.catalog_poblaciones')"
        :value="formatNum(stats.catalog.poblaciones)"
        icon="MapPin"
        variant="warning"
        :sub-value="geoCoverageLabel || undefined"
      />
      <StatCard
        :title="t('dashboard.genealogy.catalog_parroquias')"
        :value="formatNum(stats.catalog.parroquias)"
        icon="Church"
        variant="warning"
      />
    </div>

    <div class="grid grid-cols-12 gap-6 mt-6">
      <div class="col-span-12 lg:col-span-6 box p-5">
        <h3 class="font-medium text-slate-800 mb-4">{{ t("dashboard.genealogy.events_by_type") }}</h3>
        <div class="space-y-4">
          <div v-for="bar in eventTypeBars" :key="bar.tipo" class="flex items-center gap-3">
            <span class="w-28 text-sm text-slate-800 shrink-0">{{ bar.tipo }}</span>
            <div class="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all"
                :class="bar.color"
                :style="{ width: `${bar.pct}%` }"
              />
            </div>
            <span class="w-24 text-sm font-medium text-right tabular-nums">{{ formatNum(bar.count) }}</span>
          </div>
        </div>
      </div>

      <div class="col-span-12 lg:col-span-6 box p-5">
        <h3 class="font-medium text-slate-800 mb-4">{{ t("dashboard.genealogy.sex_distribution") }}</h3>
        <div class="space-y-3">
          <div class="flex items-center gap-3">
            <span class="w-20 text-sm text-slate-800">{{ t("dashboard.genealogy.sex_male") }}</span>
            <div class="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                class="h-full bg-blue-500 rounded-full"
                :style="{
                  width: `${sexTotal ? (maleCount / sexTotal) * 100 : 0}%`,
                }"
              />
            </div>
            <span class="w-24 text-sm font-medium text-right tabular-nums">{{
              formatNum(maleCount)
            }}</span>
          </div>
          <div class="flex items-center gap-3">
            <span class="w-20 text-sm text-slate-800">{{ t("dashboard.genealogy.sex_female") }}</span>
            <div class="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                class="h-full bg-pink-500 rounded-full"
                :style="{
                  width: `${sexTotal ? (femaleCount / sexTotal) * 100 : 0}%`,
                }"
              />
            </div>
            <span class="w-24 text-sm font-medium text-right tabular-nums">{{
              formatNum(femaleCount)
            }}</span>
          </div>
          <div v-if="unknownSexCount > 0" class="flex items-center gap-3">
            <span class="w-20 text-sm text-slate-800">{{ t("dashboard.genealogy.sex_unknown") }}</span>
            <div class="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                class="h-full bg-slate-400 rounded-full"
                :style="{
                  width: `${sexTotal ? (unknownSexCount / sexTotal) * 100 : 0}%`,
                }"
              />
            </div>
            <span class="w-24 text-sm font-medium text-right tabular-nums">{{
              formatNum(unknownSexCount)
            }}</span>
          </div>
        </div>
        <p class="text-xs text-slate-600 mt-4">
          {{ t("dashboard.genealogy.exact_dates", { count: formatNum(stats.events.with_exact_date) }) }}
        </p>
      </div>

      <div class="col-span-12 lg:col-span-6 box p-5">
        <h3 class="font-medium text-slate-800 mb-4">{{ t("dashboard.genealogy.top_poblaciones") }}</h3>
        <ul v-if="stats.top_poblaciones.length" class="space-y-3">
          <li v-for="(row, idx) in stats.top_poblaciones" :key="row.nombre" class="flex items-center gap-3">
            <span class="w-5 text-xs text-slate-600 tabular-nums">{{ idx + 1 }}</span>
            <span class="flex-1 text-sm truncate" :title="row.nombre">{{ row.nombre }}</span>
            <div class="w-24 h-2 bg-slate-100 rounded-full overflow-hidden shrink-0">
              <div
                class="h-full bg-warning/80 rounded-full"
                :style="{ width: `${(row.count / maxTopCount) * 100}%` }"
              />
            </div>
            <span class="w-20 text-sm font-medium text-right tabular-nums">{{ formatNum(row.count) }}</span>
          </li>
        </ul>
        <p v-else class="text-sm text-slate-700">{{ t("dashboard.genealogy.no_data") }}</p>
      </div>

      <div class="col-span-12 lg:col-span-6 box p-5">
        <h3 class="font-medium text-slate-800 mb-4">{{ t("dashboard.genealogy.top_parroquias") }}</h3>
        <ul v-if="stats.top_parroquias.length" class="space-y-3">
          <li v-for="(row, idx) in stats.top_parroquias" :key="row.nombre" class="flex items-center gap-3">
            <span class="w-5 text-xs text-slate-600 tabular-nums">{{ idx + 1 }}</span>
            <span class="flex-1 text-sm truncate" :title="row.nombre">{{ row.nombre }}</span>
            <div class="w-24 h-2 bg-slate-100 rounded-full overflow-hidden shrink-0">
              <div
                class="h-full bg-primary/80 rounded-full"
                :style="{ width: `${(row.count / maxTopCount) * 100}%` }"
              />
            </div>
            <span class="w-20 text-sm font-medium text-right tabular-nums">{{ formatNum(row.count) }}</span>
          </li>
        </ul>
        <p v-else class="text-sm text-slate-700">{{ t("dashboard.genealogy.no_data") }}</p>
      </div>
    </div>
  </div>
</template>
